import { useEffect, useMemo, useState } from 'react'
import { BlockedBadge } from './BlockedBadge'
import { useQueryClient } from '@tanstack/react-query'
import {
  X,
  TrendingUp,
  User,
  Receipt as ReceiptIcon,
  Award,
  IdCard,
  ShieldCheck,
  Star,
  CheckCircle2,
  Clock,
  Wallet,
  Tag,
  ChevronDown,
  ChevronRight,
  Loader2,
  RotateCcw,
  Save,
  AlertCircle,
} from 'lucide-react'
import {
  useCustomer,
  useCustomerInsights,
  useCustomerActivity,
  useCustomerOverview,
  useCustomerGroupsList,
  useCustomerSpringbig,
  useUpdateCustomerOverview,
  type CustomerInsights,
  type CustomerActivityEntry,
  type CustomerOverview,
  type SpringbigRedemption,
} from '@/api/customers'
import { LoyaltyRedemptionPicker } from './LoyaltyRedemptionPicker'
import { useStoresList } from '@/api/stores'
import { productKeys } from '@/api/products'
import { v2Unscoped, v2api } from '@/api/v2'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { useSelectCustomer } from '@/hooks'
import { formatCurrency } from '@/lib/utils'
import type { Customer, Product } from '@/types'
import { OrderDetailModal } from '@/components/orders/OrderDetailModal'

// ---------- Helpers ----------

function getDisplayName(customer: Customer | CustomerOverview): string {
  if (customer.first_name) {
    return `${customer.first_name} ${customer.last_name ?? ''}`.trim()
  }
  return (
    customer.display_name ||
    customer.full_name ||
    ('name' in customer ? customer.name : undefined) ||
    'Customer'
  )
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function getTier(customer: Customer): string {
  return customer.price_tier || customer.priceTier || 'Standard'
}

const tierStyles: Record<string, string> = {
  VIP: 'bg-purple-100 text-purple-700',
  'VIP (20%)': 'bg-purple-100 text-purple-700',
  Gold: 'bg-yellow-100 text-yellow-700',
  Silver: 'bg-gray-200 text-gray-700',
  Standard: 'bg-gray-100 text-gray-600',
  'Margin (15%)': 'bg-blue-100 text-blue-700',
  Medical: 'bg-green-100 text-green-700',
  Wholesale: 'bg-orange-100 text-orange-700',
}

// Backend returns mixed shapes: ISO ("2025-12-14T12:21:32"), ISO+offset, or
// US-style "MM/DD/YYYY". This handles all three.
function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const iso = new Date(value)
  if (!Number.isNaN(iso.getTime())) return iso
  const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (m) {
    return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
  }
  return null
}

function formatDate(value: string | null | undefined): string {
  const d = parseDate(value)
  if (!d) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatRelative(value: string | null | undefined): string {
  const d = parseDate(value)
  if (!d) return 'Never'
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24))
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 365) return `${days} days ago`
  return `${Math.floor(days / 365)} yr ago`
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

// Native <input type="date"> requires YYYY-MM-DD. Coerce any of the date
// shapes the backend returns (ISO, ISO+offset, MM/DD/YYYY) into that.
//
// We extract the date parts as a STRING and never round-trip through
// `new Date()`. `new Date("2026-06-03")` parses as UTC midnight, and the
// local `getDate()/getMonth()` getters then read it back a day earlier in
// any UTC-negative timezone -- so a save+reseed would silently shift the
// birth date to the previous day (and persist it on the next save).
function toDateInputValue(value: string | null | undefined): string {
  if (!value) return ''
  const s = String(value).trim()
  // ISO / ISO+offset / bare date: take the literal YYYY-MM-DD prefix.
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  // US-style MM/DD/YYYY.
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`
  return ''
}

// ---------- Component ----------

export type CustomerModalTabId = 'insights' | 'profile' | 'orders' | 'loyalty'

interface TabDef {
  id: CustomerModalTabId
  label: string
  icon: typeof TrendingUp
}

const TABS: TabDef[] = [
  { id: 'insights', label: 'Insights', icon: TrendingUp },
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'orders', label: 'Orders', icon: ReceiptIcon },
  { id: 'loyalty', label: 'Loyalty', icon: Award },
]

export interface CustomerModalProps {
  customer: Customer
  onClose: () => void
  // Tab to open the modal on. Defaults to 'insights'. Used by the
  // cart-header reward-available icon to land directly on the Loyalty
  // tab so the cashier doesn't have to click through.
  initialTab?: CustomerModalTabId
  // Insights cross-links — when provided, top brands/categories/items become
  // shortcuts to the products tab. The modal closes itself before invoking.
  onShopByBrand?: (brandName: string) => void
  onShopByCategory?: (categoryName: string) => void
  onOpenProduct?: (product: Product) => void
}

export function CustomerModal({
  customer: seed,
  onClose,
  initialTab,
  onShopByBrand,
  onShopByCategory,
  onOpenProduct,
}: CustomerModalProps) {
  const [activeTab, setActiveTab] = useState<CustomerModalTabId>(initialTab ?? 'insights')
  // `useCustomerActivity` sources from /customers/{id}/receipts (with
  // /receipts/search as the old-portal fallback), so the entry's `id` is
  // a *receipt* id (not a transaction id). Earlier code routed it into
  // OrderDetailModal as `transactionId`, which then 404'd on
  // /transactions/:id. Track it as `receiptId` here so the modal hits
  // the right endpoint.
  const [openOrder, setOpenOrder] = useState<{
    receiptId: number
    receipt_number: string
    // Originating store, from the activity row. OrderDetailModal uses it
    // to go view-only for receipts rung at another store.
    storeId: number | null
    storeName: string | null
  } | null>(null)

  const { data: freshCustomer } = useCustomer(seed.id)
  const customer = freshCustomer ?? seed

  const { data: overview } = useCustomerOverview(seed.id)
  const { data: insights, isLoading: insightsLoading } = useCustomerInsights(seed.id)
  const {
    data: activityPages,
    isLoading: activityLoading,
    isError: activityError,
    refetch: refetchActivity,
    fetchNextPage: fetchMoreActivity,
    hasNextPage: hasMoreActivity,
    isFetchingNextPage: activityLoadingMore,
  } = useCustomerActivity(seed.id)
  // Pages are appended, never merged: the server returns newest-first and
  // each page is a fresh offset window, so concatenating preserves order.
  const activity = useMemo(
    () => activityPages?.pages.flatMap((p) => p.entries) ?? [],
    [activityPages],
  )

  const { selectedCustomer } = usePOSStore()
  const selectCustomer = useSelectCustomer()
  const isActive = selectedCustomer?.id === customer.id

  const name = getDisplayName(overview ?? customer)
  const initials = getInitials(name)
  const tier = getTier(customer)
  const tierClass = tierStyles[tier] || tierStyles.Standard
  const programName = overview?.group?.name

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---------- Persistent header ---------- */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-green-100 text-green-700 flex items-center justify-center font-semibold text-lg shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 leading-tight truncate">{name}</h2>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {(overview?.is_blocked ?? customer.is_blocked) && (
                  <span className="text-xs"><BlockedBadge /></span>
                )}
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${tierClass}`}>{tier}</span>
                {customer.is_medical && (
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                    Medical
                  </span>
                )}
                {programName && (
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                    {programName}
                  </span>
                )}
                <span className="text-xs text-gray-400">#{customer.id}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* ---------- Body: nav rail + content + footer ---------- */}
        <div className="flex flex-1 min-h-0">
          <nav className="w-20 bg-gray-50 border-r border-gray-100 py-3 flex flex-col gap-1 shrink-0">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const isActiveTab = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`mx-2 py-2.5 rounded-xl flex flex-col items-center gap-1 transition-colors cursor-pointer ${
                    isActiveTab
                      ? 'bg-white text-green-600 shadow-sm border border-gray-100'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'
                  }`}
                  aria-label={tab.label}
                  title={tab.label}
                >
                  <Icon size={20} />
                  <span className="text-[10px] font-medium leading-none">{tab.label}</span>
                </button>
              )
            })}
          </nav>

          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex-1 overflow-y-auto p-5">
              {activeTab === 'insights' && (
                <InsightsPanel
                  insights={insights}
                  isLoading={insightsLoading}
                  overview={overview}
                  onShopByBrand={
                    onShopByBrand
                      ? (name) => {
                          onShopByBrand(name)
                          onClose()
                        }
                      : undefined
                  }
                  onShopByCategory={
                    onShopByCategory
                      ? (name) => {
                          onShopByCategory(name)
                          onClose()
                        }
                      : undefined
                  }
                  onOpenProduct={
                    onOpenProduct
                      ? (product) => {
                          onOpenProduct(product)
                          onClose()
                        }
                      : undefined
                  }
                />
              )}
              {activeTab === 'profile' && (
                <ProfilePanel customer={customer} overview={overview} />
              )}
              {activeTab === 'orders' && (
                <OrdersPanel
                  activity={activity}
                  isLoading={activityLoading}
                  isError={activityError}
                  onRetry={() => { void refetchActivity() }}
                  hasMore={hasMoreActivity}
                  isLoadingMore={activityLoadingMore}
                  onLoadMore={() => { void fetchMoreActivity() }}
                  onOpenOrder={(order) =>
                    setOpenOrder({
                      receiptId: order.id,
                      receipt_number: order.receipt_number,
                      storeId: order.store_id,
                      storeName: order.store_name,
                    })
                  }
                />
              )}
              {activeTab === 'loyalty' && (
                <LoyaltyPanel customer={customer} overview={overview} onClose={onClose} />
              )}
            </div>

            <div className="border-t border-gray-100 p-4 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={onClose}
                className="text-sm px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer"
              >
                Close
              </button>
              {isActive ? (
                <span className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 border border-green-200 rounded-xl text-sm font-medium">
                  <CheckCircle2 size={16} /> Active customer
                </span>
              ) : (
                <button
                  onClick={() => {
                    // Close only on successful attach -- a blocked
                    // customer change (partial-payment lock) should
                    // leave the modal up so the cashier sees why.
                    selectCustomer.mutate(customer, {
                      onSuccess: onClose,
                    })
                  }}
                  className="text-sm px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors cursor-pointer"
                >
                  Add to Order
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {openOrder && (
        <OrderDetailModal
          receiptId={openOrder.receiptId}
          receiptNumber={openOrder.receipt_number}
          originStoreId={openOrder.storeId}
          originStoreName={openOrder.storeName}
          onClose={() => setOpenOrder(null)}
        />
      )}
    </div>
  )
}

// ---------- Insights ----------

interface InsightsPanelProps {
  insights?: CustomerInsights
  isLoading: boolean
  overview?: CustomerOverview
  onShopByBrand?: (brandName: string) => void
  onShopByCategory?: (categoryName: string) => void
  onOpenProduct?: (product: Product) => void
}

function InsightsPanel({
  insights,
  isLoading,
  overview,
  onShopByBrand,
  onShopByCategory,
  onOpenProduct,
}: InsightsPanelProps) {
  const queryClient = useQueryClient()
  const showToast = useUIStore((s) => s.showToast)
  const summary = insights?.summary
  const items = insights?.items ?? []

  const [loadingProductId, setLoadingProductId] = useState<number | null>(null)

  if (isLoading && !insights) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-gray-400">
        Loading insights…
      </div>
    )
  }

  const hasBrands = !!summary?.top_brands?.length
  const hasCategories = !!summary?.top_categories?.length

  async function handleOpenProduct(productId: number) {
    if (!onOpenProduct) return
    setLoadingProductId(productId)
    try {
      const product = await queryClient.fetchQuery({
        queryKey: productKeys.detail(productId),
        queryFn: async (): Promise<Product> =>
          v2api.get<Product>(v2Unscoped(`/objects/products/${productId}`)),
        staleTime: 1000 * 60 * 5,
      })
      onOpenProduct(product)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not open product.'
      showToast({ type: 'error', title: 'Product unavailable', message })
    } finally {
      setLoadingProductId(null)
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2.5">
        <Stat
          label="Last order"
          value={formatRelative(summary?.last_order ?? overview?.last_purchase_date)}
        />
        <Stat label="Orders" value={String(summary?.order_count ?? '—')} />
        <Stat
          label="Avg items"
          value={
            summary?.avg_items_per_order !== undefined
              ? String(summary.avg_items_per_order)
              : '—'
          }
        />
      </div>

      {(hasBrands || hasCategories) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {hasBrands && (
            <Section icon={Star} title="Top brands">
              <ol className="space-y-1">
                {summary!.top_brands.slice(0, 5).map((b, idx) => (
                  <RankedRow
                    key={b.id}
                    index={idx + 1}
                    label={b.name}
                    qty={b.qty}
                    onClick={onShopByBrand ? () => onShopByBrand(b.name) : undefined}
                  />
                ))}
              </ol>
            </Section>
          )}

          {hasCategories && (
            <Section icon={Tag} title="Top categories">
              <ol className="space-y-1">
                {summary!.top_categories.slice(0, 5).map((c, idx) => (
                  <RankedRow
                    key={c.name}
                    index={idx + 1}
                    label={c.name}
                    qty={c.qty}
                    onClick={onShopByCategory ? () => onShopByCategory(c.name) : undefined}
                  />
                ))}
              </ol>
            </Section>
          )}
        </div>
      )}

      {items.length > 0 && (
        <Section icon={ReceiptIcon} title={`Frequently purchased (${items.length})`}>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {items.slice(0, 8).map((it) => {
              const isLoadingThis = loadingProductId === it.product_id
              const interactive = !!onOpenProduct
              return (
                <li key={it.product_id}>
                  <button
                    type="button"
                    onClick={interactive ? () => handleOpenProduct(it.product_id) : undefined}
                    disabled={!interactive || isLoadingThis}
                    className={`group w-full text-left flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg transition-colors ${
                      interactive
                        ? 'bg-gray-50 hover:bg-white border border-transparent hover:border-gray-200 cursor-pointer disabled:opacity-60'
                        : 'bg-gray-50 cursor-default'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                        {it.product_name}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate mt-0.5">
                        {it.brand_name || it.sku}
                        {it.unit_name ? ` · ${it.unit_name}` : ''} ·{' '}
                        {formatRelative(it.last_purchased)}
                      </p>
                    </div>
                    <span className="flex items-center gap-1 shrink-0">
                      <span className="text-[11px] font-semibold text-gray-700 bg-white border border-gray-100 px-1.5 py-0.5 rounded-full tabular-nums">
                        ×{it.total_qty}
                      </span>
                      {interactive &&
                        (isLoadingThis ? (
                          <Loader2
                            size={12}
                            className="text-gray-400 animate-spin"
                          />
                        ) : (
                          <ChevronRight
                            size={12}
                            className="text-gray-300 group-hover:text-green-600 transition-colors"
                          />
                        ))}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </Section>
      )}

      {!isLoading && !summary && items.length === 0 && (
        <div className="p-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-center">
          <Clock size={20} className="mx-auto text-gray-300 mb-1" />
          <p className="text-sm font-medium text-gray-500">No purchase history yet</p>
        </div>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-2.5 rounded-xl bg-gray-50">
      <p className="text-[11px] text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
      <p className="font-bold text-[15px] mt-0.5 leading-tight tabular-nums truncate text-gray-900">
        {value}
      </p>
    </div>
  )
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof TrendingUp
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
        <Icon size={12} />
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

function RankedRow({
  index,
  label,
  qty,
  onClick,
}: {
  index: number
  label: string
  qty: number
  onClick?: () => void
}) {
  const content = (
    <>
      <span className="flex items-center gap-2 min-w-0">
        <span className="w-4 h-4 rounded-full bg-white text-gray-500 text-[10px] font-semibold flex items-center justify-center shrink-0 tabular-nums">
          {index}
        </span>
        <span className="text-sm text-gray-800 truncate">{label}</span>
      </span>
      <span className="flex items-center gap-1 shrink-0">
        <span className="text-[11px] font-semibold text-gray-500 tabular-nums">×{qty}</span>
        {onClick && (
          <ChevronRight
            size={12}
            className="text-gray-300 group-hover:text-green-600 transition-colors"
          />
        )}
      </span>
    </>
  )

  if (!onClick) {
    return (
      <li className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
        {content}
      </li>
    )
  }

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="group w-full flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 hover:bg-white border border-transparent hover:border-gray-200 rounded-lg transition-colors cursor-pointer text-left"
      >
        {content}
      </button>
    </li>
  )
}

// ---------- Profile (editable) ----------

interface ProfileFormState {
  first_name: string
  last_name: string
  email: string
  phone: string
  birth_date: string // YYYY-MM-DD for <input type="date">
  group_id: string // '' = none; otherwise stringified id (HTML selects always emit strings)
  home_store: string
  comments: string
}

function buildInitialForm(
  customer: Customer,
  overview?: CustomerOverview
): ProfileFormState {
  return {
    first_name: overview?.first_name ?? customer.first_name ?? '',
    last_name: overview?.last_name ?? customer.last_name ?? '',
    email: overview?.email ?? customer.email ?? '',
    phone: overview?.phone ?? customer.phone ?? '',
    birth_date: toDateInputValue(overview?.birth_date ?? customer.birthdate),
    group_id: overview?.group?.id != null ? String(overview.group.id) : '',
    home_store: overview?.home_store != null ? String(overview.home_store) : '',
    comments: overview?.comments ?? '',
  }
}

function ProfilePanel({
  customer,
  overview,
}: {
  customer: Customer
  overview?: CustomerOverview
}) {
  const showToast = useUIStore((s) => s.showToast)
  const { mutateAsync: updateOverview, isPending: isSaving } = useUpdateCustomerOverview()
  const { data: stores = [] } = useStoresList()
  const { data: groups = [] } = useCustomerGroupsList()

  const initial = useMemo(() => buildInitialForm(customer, overview), [customer, overview])
  const [form, setForm] = useState<ProfileFormState>(initial)

  // Re-seed when overview finishes loading or refetches.
  useEffect(() => {
    setForm(initial)
  }, [initial])

  const isDirty = useMemo(
    () => (Object.keys(initial) as (keyof ProfileFormState)[]).some((k) => form[k] !== initial[k]),
    [form, initial]
  )

  const setField = <K extends keyof ProfileFormState>(key: K, value: ProfileFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const handleReset = () => setForm(initial)

  const handleSave = async () => {
    if (!isDirty) return
    try {
      await updateOverview({
        id: customer.id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        birth_date: form.birth_date || null,
        group_id: form.group_id ? Number(form.group_id) : null,
        home_store: form.home_store ? Number(form.home_store) : null,
        // Only sent when edited: the box is empty until the overview loads,
        // and an unrelated save in that window must not blank the note.
        comments: form.comments !== initial.comments ? form.comments.trim() : undefined,
      })
      showToast({ type: 'success', title: 'Profile updated' })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save changes.'
      showToast({ type: 'error', title: 'Save failed', message })
    }
  }

  const memberSince = overview?.start_date ?? null
  const flowersLimit =
    overview?.flowers_limit !== undefined ? toNumber(overview.flowers_limit) : undefined
  const medicalCard = overview?.medical_card_id || customer.medical_card_number || ''
  const medicalExpiry = overview?.medical_card_date_exp || customer.medical_card_expiry || ''
  const activeAilments = (overview?.ailments ?? []).filter((a) => a.value)
  const otherNotes = overview?.notes || overview?.additional_notes || customer.notes || ''
  const isMedical = customer.is_medical || medicalCard.length > 0 || activeAilments.length > 0

  return (
    <div className="space-y-5 pb-2">
      {/* Identity */}
      <FormGroup title="Identity">
        <div className="grid grid-cols-2 gap-2.5">
          <FormField label="First name">
            <TextInput
              value={form.first_name}
              onChange={(v) => setField('first_name', v)}
              placeholder="First name"
            />
          </FormField>
          <FormField label="Last name">
            <TextInput
              value={form.last_name}
              onChange={(v) => setField('last_name', v)}
              placeholder="Last name"
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <FormField label="Birth date">
            <TextInput
              type="date"
              value={form.birth_date}
              onChange={(v) => setField('birth_date', v)}
            />
          </FormField>
          {/* Member since is read-only metadata; rendering it as a
              `FormField` keeps it visually aligned with the editable
              `Birth date` input above so the row reads cleanly. */}
          {memberSince && (
            <FormField label="Member since">
              <div className="flex items-center px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-700 select-text">
                {formatDate(memberSince)}
              </div>
            </FormField>
          )}
        </div>
      </FormGroup>

      {/* Contact */}
      <FormGroup title="Contact">
        <div className="grid grid-cols-2 gap-2.5">
          <FormField label="Email">
            <TextInput
              type="email"
              value={form.email}
              onChange={(v) => setField('email', v)}
              placeholder="customer@email.com"
            />
          </FormField>
          <FormField label="Phone">
            <TextInput
              type="tel"
              value={form.phone}
              onChange={(v) => setField('phone', v)}
              placeholder="(555) 123-4567"
            />
          </FormField>
        </div>
      </FormGroup>

      {/* Account */}
      <FormGroup title="Account">
        <div className="grid grid-cols-2 gap-2.5">
          <FormField label="Rewards program">
            <SelectInput
              value={form.group_id}
              onChange={(v) => setField('group_id', v)}
            >
              <option value="">— None —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
              {/* Preserve current selection even if not in list */}
              {form.group_id && !groups.some((g) => String(g.id) === form.group_id) && (
                <option value={form.group_id}>
                  {overview?.group?.name ?? `Group #${form.group_id}`}
                </option>
              )}
            </SelectInput>
          </FormField>
          <FormField label="Home store">
            <SelectInput
              value={form.home_store}
              onChange={(v) => setField('home_store', v)}
            >
              <option value="">— None —</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
              {form.home_store && !stores.some((s) => String(s.id) === form.home_store) && (
                <option value={form.home_store}>Store #{form.home_store}</option>
              )}
            </SelectInput>
          </FormField>
        </div>
      </FormGroup>

      {/* Stored in `comments`, the field the portal labels "Customer Notes".
          Sits above the save bar so edits read as part of the form. */}
      <label className="block p-3 bg-amber-50 border border-amber-100 rounded-xl focus-within:ring-2 focus-within:ring-amber-300 transition-shadow">
        <span className="block text-[11px] text-amber-700 font-semibold uppercase tracking-wide mb-1">
          Customer notes
        </span>
        <textarea
          value={form.comments}
          onChange={(e) => setField('comments', e.target.value)}
          placeholder="Add a note about this customer"
          rows={2}
          className="block w-full resize-y bg-transparent text-sm text-amber-900 leading-relaxed placeholder:text-amber-500 focus:outline-none"
        />
      </label>

      {/* Save bar — only when dirty */}
      {isDirty && (
        <div className="sticky bottom-0 -mx-1 px-1 pt-2 pb-1 bg-gradient-to-t from-white via-white to-white/0">
          <div className="flex items-center justify-between gap-2 p-2 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-xs text-amber-800 font-medium pl-1">Unsaved changes</p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleReset}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                <RotateCcw size={12} /> Reset
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Save size={12} />
                )}
                {isSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Read-only context below the form. Member since used to live
          here too; it now sits next to Birth date in the Identity row.
          Daily flower limit stays here because it's a medical/cannabis
          compliance display, not part of the identity surface. */}
      {flowersLimit !== undefined && flowersLimit > 0 && (
        <div className="grid grid-cols-2 gap-2.5">
          <ReadOnlyRow label="Daily flower limit" value={`${flowersLimit}g`} />
        </div>
      )}

      {isMedical && (
        <FormGroup title="Medical">
          <div className="grid grid-cols-2 gap-2.5">
            <ReadOnlyRow icon={IdCard} label="Medical card" value={medicalCard || '—'} />
            <ReadOnlyRow
              icon={ShieldCheck}
              label="Card expires"
              value={formatDate(medicalExpiry)}
            />
          </div>
          {activeAilments.length > 0 && (
            <div className="p-3 bg-gray-50 rounded-xl">
              <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1.5">Ailments</p>
              <div className="flex items-center gap-1.5 flex-wrap">
                {activeAilments.map((a) => (
                  <span
                    key={a.ailment}
                    className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-medium"
                  >
                    {a.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </FormGroup>
      )}

      {customer.tags && customer.tags.length > 0 && (
        <div className="p-3 bg-gray-50 rounded-xl">
          <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1.5">Tags</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {customer.tags.map((t) => (
              <span
                key={t}
                className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs font-medium"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Invoice / medical notes stay portal-edited; shown so they don't
          disappear from the POS now that the box above is `comments` only. */}
      {otherNotes && (
        <div className="p-3 bg-gray-50 rounded-xl">
          <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Other notes</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{otherNotes}</p>
        </div>
      )}
    </div>
  )
}

// ---------- Profile form primitives ----------

function FormGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{title}</h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  )
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  )
}

function TextInput({
  type = 'text',
  value,
  onChange,
  placeholder,
}: {
  type?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
    />
  )
}

function SelectInput({
  value,
  onChange,
  children,
}: {
  value: string
  onChange: (v: string) => void
  children: React.ReactNode
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none px-3 py-2 pr-9 text-sm bg-gray-100 hover:bg-gray-200 focus:bg-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer"
      >
        {children}
      </select>
      <ChevronDown
        size={14}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      />
    </div>
  )
}

function ReadOnlyRow({
  icon: Icon,
  label,
  value,
}: {
  icon?: typeof User
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 bg-gray-50 rounded-lg">
      {Icon && <Icon size={14} className="text-gray-400 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
        <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
      </div>
    </div>
  )
}

// ---------- Orders ----------

function OrdersPanel({
  activity,
  isLoading,
  isError,
  onRetry,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onOpenOrder,
}: {
  activity: CustomerActivityEntry[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  onOpenOrder: (order: CustomerActivityEntry) => void
}) {
  // "This store only" is a CLIENT filter over the pages already loaded —
  // the route pages chain-wide and we don't want a toggle to re-fetch
  // from offset 0. Rows whose store is unknown (old portal, no store_id)
  // count as this store: that fallback only ever returns local receipts.
  const [thisStoreOnly, setThisStoreOnly] = useState(false)
  const hasForeign = useMemo(() => activity.some((o) => o.is_foreign), [activity])

  // Sorted newest-first defensively (the API already returns this order, but
  // do not rely on it).
  const sorted = useMemo(
    () =>
      [...activity]
        .filter((o) => !thisStoreOnly || !o.is_foreign)
        .sort((a, b) => {
          const ta = parseDate(a.timestamp)?.getTime() ?? 0
          const tb = parseDate(b.timestamp)?.getTime() ?? 0
          return tb - ta
        }),
    [activity, thisStoreOnly]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-gray-400">
        Loading orders…
      </div>
    )
  }

  // A failed fetch used to fall through to "No past orders" — the cashier
  // read a backend error as "this customer has never bought anything".
  // Only when nothing loaded at all: a page-2 failure keeps the rows and
  // surfaces its own retry beside the Load more button below.
  if (isError && activity.length === 0) {
    return (
      <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
        <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-red-900">Couldn't load orders</p>
          <p className="text-xs text-red-700 mt-0.5">
            The order history didn't come back. Nothing was changed — it's safe to retry.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 text-red-700 hover:bg-red-50 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
          >
            <RotateCcw size={12} /> Retry
          </button>
        </div>
      </div>
    )
  }

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
        <ReceiptIcon size={32} className="mb-2" />
        <p className="text-sm font-medium">
          {thisStoreOnly ? 'No orders from this store' : 'No past orders'}
        </p>
        {thisStoreOnly && activity.length > 0 && (
          <button
            type="button"
            onClick={() => setThisStoreOnly(false)}
            className="mt-2 text-xs font-semibold text-green-700 hover:underline cursor-pointer"
          >
            Show all stores
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Only offered once a foreign row is actually on screen — at a
          single-store chain the toggle would filter nothing. */}
      {hasForeign && (
        <label className="flex items-center justify-end gap-2 pb-1 text-xs text-gray-500 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={thisStoreOnly}
            onChange={(e) => setThisStoreOnly(e.target.checked)}
            className="accent-green-600 cursor-pointer"
          />
          This store only
        </label>
      )}
      {sorted.map((order) => (
        <button
          key={order.id}
          onClick={() => onOpenOrder(order)}
          className="w-full flex items-center justify-between gap-3 p-3 bg-gray-50 hover:bg-white border border-transparent hover:border-gray-200 rounded-xl text-left transition-colors cursor-pointer group"
        >
          <div className="min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate flex items-center gap-2">
              <span className="truncate">#{order.receipt_number}</span>
              {/* Receipts from every store are listed; label the ones rung
                  elsewhere. They open view-only (refund / void / reprint
                  belong to that store). */}
              {order.is_foreign && (
                <span
                  className="shrink-0 px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700"
                  title="Rung at another store — view only here"
                >
                  {order.store_name ?? `Store ${order.store_id}`}
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500">
              {formatDate(order.timestamp)} · {formatRelative(order.timestamp)}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <p className="text-sm font-semibold text-gray-900 tabular-nums">
              {formatCurrency(order.jamount)}
            </p>
            <span className="text-[11px] font-medium text-gray-400 group-hover:text-green-600 transition-colors">
              View →
            </span>
          </div>
        </button>
      ))}

      {/* Paging is server-side and unfiltered, so "Load more" keeps
          working while "This store only" is on — it just may add rows
          the filter then hides. */}
      {isError && activity.length > 0 && (
        <button
          type="button"
          onClick={onRetry}
          className="w-full py-2.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-100 hover:bg-red-100 rounded-xl transition-colors cursor-pointer inline-flex items-center justify-center gap-1.5"
        >
          <RotateCcw size={12} /> Couldn't load more — retry
        </button>
      )}
      {!isError && hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={isLoadingMore}
          className="w-full py-2.5 text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
        >
          {isLoadingMore ? <Loader2 size={12} className="animate-spin" /> : null}
          {isLoadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}

// ---------- Loyalty ----------

function LoyaltyPanel({
  customer,
  overview,
  onClose,
}: {
  customer: Customer
  overview?: CustomerOverview
  onClose: () => void
}) {
  const { data: springbig, isLoading, isError } = useCustomerSpringbig(customer.id)

  // Cached fallbacks for the soft-state cards. Used when SpringBig says
  // the store isn't configured, the customer isn't enrolled, or we
  // haven't loaded yet — so the panel still shows something reasonable
  // instead of zeroes.
  const cachedPoints =
    overview?.points !== undefined
      ? toNumber(overview.points)
      : (customer.points ?? customer.loyalty_points ?? 0)
  const cachedCredit =
    overview?.account_balance !== undefined
      ? toNumber(overview.account_balance)
      : (customer.store_credit ?? 0)
  const storeBalance =
    overview?.account_balance_store !== undefined
      ? toNumber(overview.account_balance_store)
      : null
  const program = overview?.group?.name
  const tier = getTier(customer)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-gray-400">
        <Loader2 size={16} className="animate-spin mr-2" />
        Loading loyalty…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900">Loyalty data unavailable</p>
          <p className="text-xs text-amber-700 mt-0.5">
            SpringBig didn't respond. Close and reopen the drawer to retry.
          </p>
        </div>
      </div>
    )
  }

  if (!springbig?.loyalty_enabled) {
    return (
      <LoyaltySoftState
        title="Loyalty isn't configured"
        message="This store isn't set up for loyalty points."
        cachedPoints={cachedPoints}
        cachedCredit={cachedCredit}
        storeBalance={storeBalance}
        program={program}
        tier={tier}
      />
    )
  }

  if (!springbig.customer_enrolled) {
    return (
      <LoyaltySoftState
        title="Customer not enrolled"
        message="This customer is not enrolled in the Points loyalty program."
        cachedPoints={springbig.balance || cachedPoints}
        cachedCredit={cachedCredit}
        storeBalance={storeBalance}
        program={program}
        tier={tier}
      />
    )
  }

  const balance = springbig.balance
  const redemptions = springbig.redemptions ?? []

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
          <div className="flex items-center gap-2 text-green-700 mb-1">
            <Star size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">Points</span>
          </div>
          <p className="text-2xl font-bold text-green-700 tabular-nums">
            {Math.round(balance)}
          </p>
        </div>
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <div className="flex items-center gap-2 text-blue-700 mb-1">
            <Wallet size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">Account balance</span>
          </div>
          <p className="text-2xl font-bold text-blue-700 tabular-nums">
            {formatCurrency(cachedCredit)}
          </p>
        </div>
      </div>

      {storeBalance !== null && storeBalance !== cachedCredit && (
        <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
          <span className="text-sm text-gray-600">Store credit</span>
          <span className="text-sm font-semibold text-gray-900 tabular-nums">
            {formatCurrency(storeBalance)}
          </span>
        </div>
      )}

      {program && (
        <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
          <span className="text-sm text-gray-600">Rewards program</span>
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
            {program}
          </span>
        </div>
      )}

      <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
        <span className="text-sm text-gray-600">Tier</span>
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${
            tierStyles[tier] || tierStyles.Standard
          }`}
        >
          {tier}
        </span>
      </div>

      <LoyaltyRedemptionPicker customer={customer} onApplied={onClose} />

      <RedemptionHistorySection redemptions={redemptions} />
    </div>
  )
}

function RedemptionHistorySection({ redemptions }: { redemptions: SpringbigRedemption[] }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
        <Clock size={12} />
        <span>Redemption history ({redemptions.length})</span>
      </div>
      {redemptions.length === 0 ? (
        <p className="text-xs text-gray-400 px-2 py-3 bg-gray-50 rounded-lg">
          No completed redemptions yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {redemptions.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-lg"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {redemptionLabel(r)}
                </p>
                <p className="text-[11px] text-gray-500">
                  {formatDate(redemptionDate(r))}
                </p>
              </div>
              {r.points_redeemed != null && (
                <span className="text-xs font-semibold text-gray-700 tabular-nums shrink-0">
                  −{Math.round(r.points_redeemed)} pts
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function LoyaltySoftState({
  title,
  message,
  cachedPoints,
  cachedCredit,
  storeBalance,
  program,
  tier,
}: {
  title: string
  message: string
  cachedPoints: number
  cachedCredit: number
  storeBalance: number | null
  program?: string
  tier: string
}) {
  return (
    <div className="space-y-3">
      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
        <div className="flex items-start gap-2">
          <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">{title}</p>
            <p className="text-xs text-amber-700 mt-0.5">{message}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
          <div className="flex items-center gap-2 text-green-700 mb-1">
            <Star size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">Points</span>
          </div>
          <p className="text-2xl font-bold text-green-700 tabular-nums">
            {Math.round(cachedPoints)}
          </p>
        </div>
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <div className="flex items-center gap-2 text-blue-700 mb-1">
            <Wallet size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">Account balance</span>
          </div>
          <p className="text-2xl font-bold text-blue-700 tabular-nums">
            {formatCurrency(cachedCredit)}
          </p>
        </div>
      </div>

      {storeBalance !== null && storeBalance !== cachedCredit && (
        <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
          <span className="text-sm text-gray-600">Store credit</span>
          <span className="text-sm font-semibold text-gray-900 tabular-nums">
            {formatCurrency(storeBalance)}
          </span>
        </div>
      )}

      {program && (
        <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
          <span className="text-sm text-gray-600">Rewards program</span>
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
            {program}
          </span>
        </div>
      )}

      <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
        <span className="text-sm text-gray-600">Tier</span>
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${
            tierStyles[tier] || tierStyles.Standard
          }`}
        >
          {tier}
        </span>
      </div>
    </div>
  )
}

// SpringBig pass-through fields aren't strongly typed — the BE forwards
// them verbatim. These read whichever name the API happens to populate
// for a human-readable label.
function redemptionLabel(redemption: SpringbigRedemption): string {
  return (
    pickString(redemption, ['reward_name', 'name', 'title', 'description'])
    ?? `Redemption #${redemption.id}`
  )
}

function redemptionDate(redemption: SpringbigRedemption): string | null {
  return (
    pickString(redemption, ['redeemed_at', 'created_at', 'timestamp', 'date'])
    ?? null
  )
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v
  }
  return null
}
